import DisplayObject = createjs.DisplayObject;
import ColorFilter = createjs.ColorFilter;
const memoize = require('lodash.memoize');

export class ColorTransform {
    private _displayObject: DisplayObject;
    private _color = "";

    /**
     * @param _redMultiplier
     * @param _greenMultiplier
     * @param _blueMultiplier
     * @param _alphaMultiplier
     * @param _redOffset
     * @param _greenOffset
     * @param _blueOffset
     * @param _alphaOffset
     */
    public constructor(
        private _redMultiplier   : number = 1,
        private _greenMultiplier : number = 1,
        private _blueMultiplier  : number = 1,
        private _alphaMultiplier : number = 1,
        private _redOffset       : number = 0,
        private _greenOffset     : number = 0,
        private _blueOffset      : number = 0,
        private _alphaOffset     : number = 0
    ) {
        this._color = ColorTransform.rgbToHex(
            _redOffset,
            _greenOffset,
            _blueOffset,
            true
        );
    }

    public get color(): string | number { return this._color; }
    public get displayObject(): DisplayObject { return this._displayObject; }

    protected static ensureBetween(min:number, max:number, value:number) : number {
        return Math.max(min, Math.min(value, max)) | 0;
    }

    private setChannelOffset(channel: 'red' | 'green' | 'blue' | 'alpha', newValue: number) {
        newValue = ColorTransform.ensureBetween(-255,255,newValue);
        const multiplier = this.getMultiplier(channel);
        const oldValue = this.getOffset(channel);
        const updatedValue = ColorTransform.ensureBetween(
            0,255,
            ((oldValue | 0 * multiplier) + newValue) | 0
        );

        switch (channel) {
            case 'red':
                this._redOffset = updatedValue;
                break;
            case 'green':
                this._greenOffset = updatedValue;
                break;
            case 'blue':
                this._blueOffset = updatedValue;
                break;
            case 'alpha':
                this._alphaOffset = updatedValue;
                break;
        }
    }

    private getMultiplier(channel: 'red' | 'green' | 'blue' | 'alpha') {
        switch (channel) {
            case 'red':
                return this.redMultiplier;
            case 'green':
                return this.greenMultiplier;
            case 'blue':
                return this.blueMultiplier;
            case 'alpha':
                return this.alphaMultiplier;
            default:
                throw new Error(`No multiplier for "${channel}"`);
        }
    }

    private getOffset(channel: 'red' | 'green' | 'blue' | 'alpha') {
        switch (channel) {
            case 'red':
                return this.redOffset;
            case 'green':
                return this.greenOffset;
            case 'blue':
                return this.blueOffset;
            case 'alpha':
                return this.alphaOffset;
            default:
                throw new Error(`No offset for "${channel}"`);
        }
    }

    public get alphaOffset(): number {
        return this._alphaOffset;
    }

    public set alphaOffset(value : number) {
        this.setChannelOffset('alpha', value);
        this.updateColor();
    }

    public get blueMultiplier(): number {
        return this._blueMultiplier;
    }

    public set blueMultiplier(value : number) {
        this._blueMultiplier = value;
        this.updateColor();
    }

    public get blueOffset(): number {
        return this._blueOffset;
    }

    public set blueOffset(value : number) {
        this.setChannelOffset('blue', value);
        this.updateColor();
    }

    public get greenMultiplier(): number {
        return this._greenMultiplier;
    }

    public set greenMultiplier(value : number) {
        this._greenMultiplier = value;
        this.updateColor();
    }

    public get greenOffset(): number {
        return this._greenOffset;
    }

    public set greenOffset(value : number) {
        this.setChannelOffset('green', value);
        this.updateColor();
    }

    public get redMultiplier(): number {
        return this._redMultiplier;
    }

    public set redMultiplier(value : number) {
        this._redMultiplier = value;
        this.updateColor();
    }

    public get redOffset(): number {
        return this._redOffset;
    }

    public set redOffset(value : number) {
        this.setChannelOffset('red', value);
        this.updateColor();
    }

    /**
     * Setter for our alpha multiplier, after we set a new alpha it will also update our displayObject
     * @param val
     */
    public set alphaMultiplier(val: number) {
        this._alphaMultiplier = val;
        this.updateColor();
    }

    public get alphaMultiplier(): number {
        return this._alphaMultiplier;
    }

    /**
     * Set the display object which the ColorTransform is applied on
     * @param value
     */
    public set displayObject(value: DisplayObject) {
        this._displayObject = value;
    }

    /**
     * Update the color on the display object with the current values from the ColorTransform
     */
    public updateColor(): void {
        this._color = ColorTransform.rgbToHex(
            this._redOffset,
            this._greenOffset,
            this._blueOffset,
            true
        );

        if(!this._displayObject) return;

        this._displayObject.filters = [new ColorFilter(
            this._redMultiplier,
            this._greenMultiplier,
            this._blueMultiplier,
            this._alphaMultiplier,
            this._redOffset,
            this._greenOffset,
            this._blueOffset,
            this._alphaOffset
        )];
    }

    /**
     * The RGB color value for a ColorTransform object. When you set a value for this property, use the format 0xRRGGBB.
     * It changes the three color offset values (redOffset, greenOffset, blueOffset) accordingly,
     * and it sets the three color multiplier values (redMultiplier, greenMultiplier, blueMultiplier) to 0.
     * The alpha channel's multiplier and offset values don't change.
     */
     /**
     * Sets the color to the desired value on our display object
     * @param val
     */
    public set color(val: string | number) {
        val = ColorTransform.colorNumToString(val);

        this._redMultiplier = this._greenMultiplier = this._blueMultiplier = 0;

        const RGB = ColorTransform.hexToRgb(val);
        this._redOffset   = RGB.r;
        this._greenOffset = RGB.g;
        this._blueOffset  = RGB.b;

        this.updateColor();
    }

    /*--------------------------------------------------------------------------
        HELPER METHODS
    --------------------------------------------------------------------------*/
    // http://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
    public static hexToRgb = memoize(
        function(hex: string) {
            hex = hex.replace(/[^0-9A-F]/gi, "");

            let arrBuff = new ArrayBuffer(4);
            let vw = new DataView(arrBuff);
            vw.setUint32(0, parseInt(hex, 16), false);
            let arrByte = new Uint8Array(arrBuff);

            return {
                r: arrByte[1],
                g: arrByte[2],
                b: arrByte[3]
            };
        }
    )

    public static rgbToHex(r: number, g: number, b: number, addHashSign=false) {
        return (addHashSign ? '#' : '') + ColorTransform.uint32toHex(
              ((r & 0xFF) << 16)
            + ((g & 0xFF) <<  8)
            + ((b & 0xFF) <<  0)
        );
    }

    public static uint32toHex(value : number) : string {
        return    ColorTransform.uint8toHex((value & 0xFF0000) >> 16)
                + ColorTransform.uint8toHex((value & 0x00FF00) >>  8)
                + ColorTransform.uint8toHex((value & 0x0000FF) >>  0);
    }

    public static colorNumToString = memoize(
        function(color : number | string) : string {
            return typeof color !== "number"
                ?   color
                :   '#' + ColorTransform.uint32toHex(color as number);
        }
    )

    /**
     * Converts a number to hexadecimal
     * @param c
     * @returns {string}
     */
    public static uint8toHex = memoize(
        function(c: number) {
            let result = Number(c & 0xFF).toString(16).toUpperCase();
            if(c < 0x10) result = '0' + result;
            return result;
        }
    );
}

(window as any).rgbToHex = ColorTransform.rgbToHex;
